/**
 * Generate an employee's identification card, after a look at the photo.
 *
 * The photo is found for the office when it can be: the profile photo, or a picture
 * filed among the person's documents and named like one ("foto.jpg"). They see it,
 * keep it or choose another file, and "Done" makes it the profile photo and downloads
 * the card. Nothing about the person changes until then.
 */

import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { Modal } from '@/components/Modal';
import { Spinner } from '@/components/ui';
import { useI18n } from '@/i18n';
import { api, ApiError, type User } from '@/lib/api';
import { fullName } from '@/lib/format';

export function BadgeDialog({ user, open, onClose }: { user: User; open: boolean; onClose: () => void }) {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const picker = useRef<HTMLInputElement>(null);
  const [chosen, setChosen] = useState<{ file: File; preview: string } | null>(null);
  const [done, setDone] = useState<string | null>(null);

  const status = useQuery({
    queryKey: ['badge', user.id],
    queryFn: () => api.badge(user.id),
    enabled: open,
    staleTime: 0,
  });

  // A fresh start each time; the preview's object URL is released with it.
  useEffect(() => {
    if (!open) return;
    setChosen(null);
    setDone(null);
  }, [open]);
  useEffect(() => () => (chosen ? URL.revokeObjectURL(chosen.preview) : undefined), [chosen]);

  const s = status.data;
  const usableProfile = s?.photo?.usable ? s.photo : null;
  const shown = chosen
    ? { url: chosen.preview, caption: t('badge.photoChosen') }
    : usableProfile
      ? { url: usableProfile.url, caption: t('badge.photoProfile') }
      : s?.suggested
        ? { url: s.suggested.url, caption: t('badge.photoFound', { name: s.suggested.filename }) }
        : null;

  const generate = useMutation({
    mutationFn: async () => {
      // The photo first: whichever was confirmed becomes the profile photo.
      if (chosen) await api.setUserPhoto(user.id, chosen.file);
      else if (!usableProfile && s?.suggested) await api.badgePhotoFromDocument(user.id, s.suggested.documentId);

      const { blob, filename } = await api.downloadBadge(user.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
      return filename;
    },
    onSuccess: async (filename) => {
      setDone(filename);
      setChosen(null);
      await queryClient.invalidateQueries({ queryKey: ['user', user.id] });
      await queryClient.invalidateQueries({ queryKey: ['users'] });
      await queryClient.invalidateQueries({ queryKey: ['badge', user.id] });
    },
  });

  const missing = s?.missing ?? [];
  const canGenerate = Boolean(s) && missing.length === 0 && Boolean(shown) && !generate.isPending;

  return (
    <Modal
      open={open}
      onClose={() => (generate.isPending ? undefined : onClose())}
      size="sm"
      title={t('badge.dialogTitle', { name: fullName(user) })}
      description={t('badge.hint')}
      footer={
        done ? (
          <button type="button" className="btn-primary" onClick={onClose}>
            {t('common.close')}
          </button>
        ) : (
          <>
            <button type="button" className="btn-primary" disabled={!canGenerate} onClick={() => generate.mutate()}>
              {generate.isPending ? t('badge.generating') : t('badge.done')}
            </button>
            <button type="button" className="btn-secondary" disabled={generate.isPending} onClick={onClose}>
              {t('common.cancel')}
            </button>
          </>
        )
      }
    >
      {status.isLoading ? (
        <div className="flex justify-center py-6">
          <Spinner />
        </div>
      ) : (
        <div className="space-y-4">
          {missing.length > 0 ? (
            <p className="rounded bg-warning-soft px-3 py-2 text-sm text-warning">
              {t('badge.missing', { fields: missing.join(', ').toLowerCase() })}
            </p>
          ) : null}

          <div className="flex items-start gap-4">
            <div className="h-40 w-[120px] shrink-0 overflow-hidden rounded border border-border bg-surface-alt">
              {shown ? <img src={shown.url} alt="" className="h-full w-full object-cover" /> : null}
            </div>
            <div className="min-w-0 space-y-2 text-sm">
              <p className={shown ? 'font-medium' : 'text-muted'}>
                {shown ? shown.caption : s?.photo && !s.photo.usable ? t('badge.unusable') : t('badge.noPhoto')}
              </p>
              {!done ? (
                <button
                  type="button"
                  className="btn-secondary"
                  disabled={generate.isPending}
                  onClick={() => picker.current?.click()}
                >
                  {t('badge.choose')}
                </button>
              ) : null}
              <input
                ref={picker}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/heic"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  e.target.value = '';
                  if (file) setChosen({ file, preview: URL.createObjectURL(file) });
                }}
              />
            </div>
          </div>

          {done ? (
            <p className="rounded bg-success-soft px-3 py-2 text-sm font-semibold text-success">
              {t('badge.downloaded', { name: done })}
            </p>
          ) : null}
          {generate.error || status.error ? (
            <p role="alert" className="text-sm text-danger">
              {(generate.error ?? status.error) instanceof ApiError
                ? ((generate.error ?? status.error) as ApiError).message
                : t('common.saveError')}
            </p>
          ) : null}
        </div>
      )}
    </Modal>
  );
}
